import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  DealDrawerService,
  DealDrawerOptions,
} from '../services/deal-drawer.service';
import { DealFormComponent } from '../deal-form/deal-form.component';
import { DealResponse } from '../models/deal.model';

@Component({
  selector: 'app-deal-form-drawer',
  standalone: true,
  imports: [CommonModule, DealFormComponent],
  template: `
    @if (isOpen) {
      <!-- Overlay -->
      <div
        class="fixed inset-0 z-[99999] bg-black/40"
        (click)="close()"
      ></div>
    }

    <!-- Drawer panel — always in DOM so @ViewChild is available, visibility controlled by transform -->
    <div
      class="fixed top-0 right-0 z-drawer h-full w-full max-w-4xl bg-white dark:bg-gray-900 flex flex-col transition-transform duration-300 ease-in-out overflow-hidden"
      [class.shadow-2xl]="isOpen"
      [class.translate-x-0]="isOpen"
      [class.translate-x-full]="!isOpen"
    >
      <!-- Drawer header -->
      <div
        class="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0"
      >
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
          {{ options.dealId ? 'Editar Negocio' : 'Nuevo Negocio' }}
        </h2>
        <button
          type="button"
          (click)="close()"
          class="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-300 dark:hover:bg-gray-800 transition-colors"
        >
          <svg
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <!-- Form content (scrollable) -->
      <div class="flex-1 overflow-y-auto p-6">
        @if (isOpen) {
          <app-deal-form
            [drawerMode]="true"
            [prefilledContactId]="options.contactId"
            [prefilledCompanyId]="options.companyId"
            [prefilledCompanyName]="options.companyName"
            [prefilledContactName]="options.contactName"
            [prefilledLeadId]="options.leadId"
            (saved)="onSaved($event)"
            (cancelled)="onCancelled()"
          />
        }
      </div>
    </div>
  `,
})
export class DealFormDrawerComponent implements OnInit, OnDestroy {
  @ViewChild(DealFormComponent) formComponent?: DealFormComponent;

  isOpen = false;
  options: DealDrawerOptions = {};
  private readonly destroy$ = new Subject<void>();

  constructor(private drawerService: DealDrawerService) {}

  ngOnInit(): void {
    this.drawerService.state
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        const wasOpen = this.isOpen;
        this.isOpen = state.isOpen;
        this.options = state.options;

        if (state.isOpen && !wasOpen) {
          // Give ViewChild time to render, then configure the form
          setTimeout(() => {
            if (this.formComponent) {
              this.formComponent.reset();
              if (state.options.dealId) {
                this.formComponent.loadDeal(state.options.dealId);
              }
            }
          }, 0);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSaved(deal: DealResponse): void {
    this.drawerService.notifySaved(deal);
    this.drawerService.close();
  }

  onCancelled(): void {
    this.drawerService.close();
  }

  close(): void {
    this.drawerService.close();
  }
}
